import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Paper, Typography, Divider, Stack, IconButton, Tooltip } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import SelfProbationForm from './SelfProbationForm';
import ManagerProbationForm from './ManagerProbationForm';
import ProbationManagerCommentsView from './ProbationManagerCommentsView';
import { getAllCorporateUsers } from '../CorporateQueries';
import { getAllProbationReviews } from './ProbationReviewQueries';
import ProbationReviewList from './ProbationReviewList';
import ProbationReviewHeader from './ProbationReviewHeader';
import { getCorporateUser } from '../CorporateQueries';

const ProbationReviewIndex = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(0);
  const [canManagerReview, setCanManagerReview] = useState(false);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [reviewsCache, setReviewsCache] = useState([]);
  
  // Get form data from navigation state
  const formFromState = location.state?.formFromState || location.state?.formData;
  const formSchemaFromState = location.state?.formSchemaFromState || location.state?.formSchema;
  const employeeData = location.state?.employeeData;
  const openManagerTab = location.state?.openManagerTab;
  const reviewData = location.state?.reviewData; // Review data passed from table
  const isManagerView = openManagerTab; // Flag to indicate manager is reviewing
  
  const [details, setDetails] = useState({
    employeeName: '',
    title: '',
    manager: '',
    secondLevelManager: '',
    periodStart: formFromState?.start_date || '',
    periodEnd: formFromState?.end_date || '',
    reviewPeriod: '',
    date: new Date().toISOString().slice(0, 10),
    formId: formFromState?.id || ''
  });

  // Update details when formFromState changes
  useEffect(() => {
    if (formFromState) {
      setDetails(prev => ({
        ...prev,
        employeeName: employeeData?.employee_name || employeeData?.employee_email || prev.employeeName,
        periodStart: formFromState.start_date || '',
        periodEnd: formFromState.end_date || '',
        formId: formFromState.id || ''
      }));
    }
  }, [formFromState, employeeData]);

  // Open manager tab if navigating from ManagerReviewsTable
  useEffect(() => {
    if (openManagerTab && canManagerReview) {
      setTab(0); // Set to first visible tab (which is Manager Review in manager view)
      // Set the employee email from the form data
      if (employeeData?.employee_email) {
        setSelectedEmployeeEmail(employeeData.employee_email);
      }
    }
  }, [openManagerTab, canManagerReview, employeeData]);

  useEffect(() => {
    const checkManagerAccess = async () => {
      try {
        const currentUserEmail = localStorage.getItem('user') || '';
        if (!currentUserEmail) {
          setCanManagerReview(false);
          return;
        }

        const users = await getAllCorporateUsers();
        const isReportingL1 = users.some(u => String(u.reporting_l1 || '').trim().toLowerCase() === String(currentUserEmail).trim().toLowerCase());
        const isReportingL2 = users.some(u => String(u.reporting_l2 || '').trim().toLowerCase() === String(currentUserEmail).trim().toLowerCase());
        
        // Allow both L1 and L2 managers to review
        setCanManagerReview(isReportingL1 || isReportingL2);
      } catch (err) {
        console.error('[ProbationReview] Error checking manager access:', err);
        setCanManagerReview(false);
      }
    };

    checkManagerAccess();
  }, []);

  // Fetch all reviews once
  const loadAllReviews = async () => {
    try {
      const res = await getAllProbationReviews();
      const items = Array.isArray(res?.reviews)
        ? res.reviews
        : Array.isArray(res?.Items)
        ? res.Items
        : Array.isArray(res?.body?.reviews)
        ? res.body.reviews
        : Array.isArray(res?.body?.Items)
        ? res.body.Items
        : Array.isArray(res)
        ? res
        : [];
      if (items?.length) setReviewsCache(items);
    } catch (e) {
      console.error('[ProbationReview] Error loading reviews:', e);
    }
  };

  useEffect(() => {
    loadAllReviews();
  }, []);

  // Refresh reviews when switching to Manager Comments tab
  useEffect(() => {
    if (!isManagerView && tab === 1) {
      loadAllReviews();
    }
  }, [tab, isManagerView]);

  // Prevent selecting tabs that user doesn't have access to
  useEffect(() => {
    if (!canManagerReview && tab > 1) {
      setTab(0);
    }
  }, [canManagerReview, tab]);

  // Prefill header details for selected employee when in Manager Review tab or when manager is viewing
  useEffect(() => {
    const loadSelectedEmployeeDetails = async () => {
      try {
        // For managers: load when on Manager Review tab (tab 2) or in manager view mode
        // Skip loading if no employee is selected
        if (!selectedEmployeeEmail) return;
        
        // Load for managers when reviewing (tab 2 for employees with manager role, or manager view mode)
        const shouldLoad = (canManagerReview && tab === 2) || isManagerView;
        if (!shouldLoad) return;
        
        const user = await getCorporateUser(selectedEmployeeEmail);
        if (user) {
          const raw = Array.isArray(user?.users) ? user.users[0] : (user?.user || user);
          const val = (obj, key) => {
            const v = obj?.[key];
            if (v && typeof v === 'object' && 'S' in v) return v.S || '';
            return v || '';
          };

          const first = val(raw, 'first_name');
          const last = val(raw, 'last_name');
          const title = val(raw, 'job_title');
          const manager = val(raw, 'reporting_l1');
          const secondLevelManager = val(raw, 'reporting_l2');

          setDetails(prev => ({
            ...prev,
            employeeName: `${first} ${last}`.trim(),
            title,
            manager,
            secondLevelManager
          }));
        }
      } catch (err) {
        console.error('[ProbationReview] Error loading selected employee details:', err);
      }
    };
    loadSelectedEmployeeDetails();
  }, [tab, selectedEmployeeEmail, canManagerReview, isManagerView]);

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title={isManagerView ? "Back to Manager Reviews" : "Back to Reviews"}>
              <IconButton onClick={() => navigate(isManagerView ? '/ManagerReviews' : '/Reviews')} size="small">
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Probation Progress Assessment</Typography>
          </Stack>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        
        {/* Common header above tabs */}
        <ProbationReviewHeader
          details={details}
          setDetails={setDetails}
          editable={false}
          autoPrefill={!isManagerView && tab === 0}
          showEmployeeNote={!isManagerView && tab === 0}
          canSetPeriod={false}
          selectedEmployeeEmail={''}
        />

        {!isManagerView && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="probation review tabs">
            <Tab label="Self Review" />
            <Tab label="Manager Review" />
            {/* {canManagerReview && <Tab label="Manager Review" />}
            {canManagerReview && <Tab label="Reviews" />} */}
          </Tabs>
        )}
        {isManagerView && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="manager review tabs">
            <Tab label="Manager Review" />
          </Tabs>
        )}
      </Paper>

      <Box sx={{ mt: 2 }}>
        {!isManagerView && tab === 0 && (
          <SelfProbationForm 
            details={details} 
            reviewsCache={reviewsCache} 
            refreshCache={loadAllReviews}
            formSchema={formSchemaFromState}
          />
        )}
        {!isManagerView && tab === 1 && (
          <ProbationManagerCommentsView 
            reviewsCache={reviewsCache}
            formSchema={formSchemaFromState}
            periodStart={details.periodStart}
          />
        )}
        {((isManagerView && tab === 0)) && (
          <ManagerProbationForm
            disabled={!canManagerReview}
            selectedEmployeeEmail={selectedEmployeeEmail}
            selectedEmployeeId={selectedEmployeeId}
            reviewsCache={reviewsCache}
            onSelectEmployee={(email) => { setSelectedEmployeeEmail(email); setSelectedEmployeeId(''); }}
            details={details}
            refreshCache={loadAllReviews}
            formSchema={formSchemaFromState}
            hideEmployeeSelector={isManagerView}
            passedReviewData={reviewData}
          />
        )}
        {/* {!isManagerView && canManagerReview && tab === 2 && (
          <ManagerProbationForm
            disabled={!canManagerReview}
            selectedEmployeeEmail={selectedEmployeeEmail}
            selectedEmployeeId={selectedEmployeeId}
            reviewsCache={reviewsCache}
            onSelectEmployee={(email) => { setSelectedEmployeeEmail(email); setSelectedEmployeeId(''); }}
            details={details}
            refreshCache={loadAllReviews}
            formSchema={formSchemaFromState}
            hideEmployeeSelector={isManagerView}
            passedReviewData={reviewData}
          />
        )}
        {!isManagerView && canManagerReview && tab === 3 && (
          <ProbationReviewList 
            onOpen={(email, id) => { 
              setSelectedEmployeeEmail(email); 
              setSelectedEmployeeId(id || ''); 
              setTab(2); 
            }} 
            onCache={(items) => setReviewsCache(items)}
            userRole="manager"
          />
        )} */}
      </Box>
    </Box>
  );
};

export default ProbationReviewIndex;
